import React, { ReactElement, useMemo, useState } from 'react';
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { UserApi } from '../../../../../../redux/api/UserApi';
import { TicketApi } from '../../../../../../redux/api/TicketApi';
import { FieldRenderProps, Form, FormElement } from '@progress/kendo-react-form';
import { Avatar } from '@progress/kendo-react-layout';
import { toast } from 'react-toastify';
import { PartialSpinny } from '../../../../../Nav/Spinny';
import { useReduxDispatch, useReduxSelector } from '../../../../../../redux/hooks';
import Time from '../../../../../Common/Time';
import { TextBox } from '@progress/kendo-react-inputs';
import * as EmailActionSlice from '../../../../../../slices/EmailActionSlice';
import { Editor, EditorIFrameInitEvent } from '@progress/kendo-react-editor';
import { CommonTools, MobileTools } from '../../../../../Common/CommonTools';
import { EmailTemplateList } from '../../../../ListControls/EmailTemplateList';
import { IStaff } from '../../../../../../types/IStaff';
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators';
import { isMobile } from 'react-device-detect';
import FileUpload from '../../../../../Attachment/FileUpload';
import { FileApi } from '../../../../../../redux/api/FileApi';
import { FileType } from '../../../../../../types/File/FileType';

interface EmailFormProps {
    onsubmitted?: () => void
    onclosed?: () => void
    showSaveButton?: boolean
    formName?: string
}

const EmailForm: React.FC<EmailFormProps> = (props) => {
    const { ticketId } = useParams();
    const formState = useReduxSelector(state => state.emailAction);
    const { settings } = useReduxSelector(state => state.actionDetails);
    const dispatch = useReduxDispatch();
    const { data: userProfile } = UserApi.useGetUserProfileQuery();
    const [filter, setFilter] = useState<FilterDescriptor>({ operator: 'contains' })
    const [filterCC, setFilterCC] = useState<FilterDescriptor>({ operator: 'contains' })
    const [filterBCC, setFilterBCC] = useState<FilterDescriptor>({ operator: 'contains' })
    //const [form, setFilter] = useState
    const { data: isFileActiveInternal } = FileApi.useIsActiveQuery(FileType.Call_Action_Internal)
    let { data: ToList, isFetching: toLoading } = UserApi.useSearchContactsQuery(filter?.value, { skip: !filter?.value || filter?.value?.length < 3 });
    let { data: CCList, isFetching: ccLoading } = UserApi.useSearchContactsQuery(filter?.value, { skip: !filter?.value || filter?.value?.length < 3 });
    let { data: BCCList, isFetching: bccLoading } = UserApi.useSearchContactsQuery(filter?.value, { skip: !filter?.value || filter?.value?.length < 3 });
    const { data: template, isFetching: loadingTemplate, error: fetchTemplateError } = TicketApi.useGetQuickNoteAsActionQuery({ id: formState?.email?.Template_Id, ticketId: parseInt(ticketId) }, { skip: !formState?.email?.Template_Id })
    const [sendEmailAction, { isLoading: sendingEmail, error: sendEmailError }] = TicketApi.useSendEmailMutation();
    const filteredTo = useMemo(() => ToList?.length && filter?.value ? filterBy(ToList?.slice(), filter) : ToList, [filter, ToList]);
    const filteredCC = useMemo(() => CCList?.length && filterCC?.value ? filterBy(CCList?.slice(), filterCC) : CCList, [filterCC, CCList]);
    const filteredBCC = useMemo(() => BCCList?.length && filterBCC?.value ? filterBy(BCCList?.slice(), filterBCC) : BCCList, [BCCList, BCCList]);

    const { data: ticket } = TicketApi.useGetTicketQuery({ ticketId: parseInt(ticketId) })
    const { data: status } = TicketApi.useGetStatusQuery(ticket?.Call_Status_Id, { skip: !ticket?.Call_Status_Id || userProfile?.Features?.External_Interface })
    const { data: defaultFormState, isLoading } = TicketApi.useGetEmailConfigQuery(parseInt(ticketId), { skip: userProfile?.Features?.External_Interface });
    const addTimeSettings = { canAddTime: !status || status?.Allow_Add_Time, warning: `No time can be added because it is disabled for this ${userProfile?.Phrases.CallPhrase} Status (${status?.Call_Status})` }

    const onFilter = (event: MultiSelectFilterChangeEvent) => {
        //console.log('Filter', event);
        //setStaff(filterBy(staffList?.slice(), event.filter));
        setFilter(event.filter);
    }

    //Load the default form state
    React.useEffect(() => {
        if (defaultFormState && formState.ticketId != parseInt(ticketId)) {
            dispatch(EmailActionSlice.setTicketId(parseInt(ticketId)));
            dispatch(EmailActionSlice.resetForm(defaultFormState));
        }
    }, [defaultFormState]);

    //Update the note when the quick note (template) changes
    React.useEffect(() => {
        if (template) {
            dispatch(EmailActionSlice.setNotes(template?.Action_Notes));
            dispatch(EmailActionSlice.setBillable(template?.Billable_Time));
            dispatch(EmailActionSlice.setNonBillable(template?.Non_Billable_Time));
        }
    }, [template]);

    const handleSend = async (values, event) => {
        event.preventDefault();
        try {
            //create a new share item            
            console.log('SENDING', formState);

            //Prevent null notes
            const sendData = { ...formState.email, HTML_Note: formState.email.HTML_Note || '' };

            sendEmailAction({ ticketId: parseInt(ticketId), info: sendData }).unwrap()
                .then(() => {

                    //nom nom toast
                    toast.success('Email sent successfully');

                    //reset the form
                    dispatch(EmailActionSlice.resetForm(defaultFormState));

                    console.log('sendEmailAction submitted', props.onsubmitted);

                    //go back
                    //navigate(-1);
                    props.onsubmitted && props.onsubmitted();

                }).catch((error) =>
                    toast.error('Error sending email: ' + JSON.stringify(error))
                )
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Error sending email: ' + JSON.stringify(error));
        }

        return false;
    };

    ///Render the 'chip' tag
    const renderTag = (tagData, tag: ReactElement) => {
        var data = tagData.data[0] as IStaff;
        var imgUrl = data?.Profile_Image_Path;
        var showIMage = tagData?.data[0].img_class != 'n';
        var customElement = <div key={data.ID} className='k-hbox' title={data.Full_Name + ' - ' + data.Company_Name}>
            {showIMage && <Avatar size='small' rounded='full' className='user-profile-image ' >
                {imgUrl?.indexOf('/images') == 0 ? <img src={`.${imgUrl}`} alt="Profile" width="25" height="25" /> : (imgUrl ? <img src={imgUrl} alt="Profile" width="25" height="25" /> : <img src={'./images/placeholder.png'} alt="Profile" width="25" height="25" />)}
            </Avatar>}
            <div className='name'>{data.Full_Name} &lt;{data.Email}&gt;</div>
        </div>

        return React.cloneElement(tag, tag.props, [
            (customElement) as any,
            tag.props.children
        ]);
    }
    const renderItem = (li, itemProps) => {
        var data = itemProps.dataItem as IStaff;
        var showIMage = true//data.img_class != 'n';
        var item = (

            <div className="k-hbox" key={data.ID}>
                {showIMage && <Avatar size='medium' rounded='full' className='user-profile-image' >
                    {data.Profile_Image_Path?.indexOf('/images') == 0 ? <img src={`.${data.Profile_Image_Path}`} alt="Profile" width="30" height="30" /> : (data.Profile_Image_Path ? <img src={data.Profile_Image_Path} alt="Profile" width="30" height="30" /> : <img src={'./images/placeholder.png'} alt="Profile" width="30" height="30" />)}
                </Avatar>}
                <div className='ms-1 flex-nope'>
                    <div className='flex'>{data.Full_Name} - {data.Company_Name}</div>
                    <small className='user-sub-title' title={data.Email}> &lt;{data.Email}&gt;</small>
                </div>
            </div>)

        return React.cloneElement(li, li.props, item);
    };

    const handleToChange = (event: MultiSelectChangeEvent) => {
        //console.log('handleToChange', event);

        const values = event.target.value;
        //console.log('values', values);

        dispatch(EmailActionSlice.setTo(validateEmails(values)));
    };
    const handleCChange = (event: MultiSelectChangeEvent) => {
        dispatch(EmailActionSlice.setCC(validateEmails(event.value)));
    };
    const handleBCChange = (event: MultiSelectChangeEvent) => {
        dispatch(EmailActionSlice.setBCC(validateEmails(event.value)));
    };

    const validateEmails = (values: IStaff[]) => {
        //Check for custom value which means ID is null and Full_Name is the email address OR the email address and name.
        //use Regex to extract the email address from between the < > or just use the Full_Name
        //const values = event.target.value;
        const lastItem = { ...values[values.length - 1] };
        if (values.length > 0 && lastItem && lastItem.ID == null && !lastItem.Email) {
            values.pop();
            var Regex = /<([^>]+)>/;
            var foundEmail = Regex.exec(lastItem?.Full_Name);

            //var name = lastItem?.Full_Name;
            if (foundEmail) {
                lastItem.Email = foundEmail[foundEmail.length - 1];
                //THe name should be the start of the string to the first < character
                lastItem.Full_Name = lastItem?.Full_Name.substring(0, lastItem?.Full_Name.indexOf('<')).trim();
            } else {
                lastItem.Email = lastItem.Full_Name;
                lastItem.Full_Name = '';
            }

            //use a regex to make sure the email is valid
            var emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            var isValid = emailRegex.test(lastItem.Email);
            console.log('isValid', isValid, lastItem);
            if (!isValid) return;

            values.push(lastItem);
        }

        return values;
    }

    const onMount = (event) => {
        //Set focus on the element in the iframe with 'k-content' as the class
        const iframeDocument = event.dom.ownerDocument;
        const element = iframeDocument.querySelector('.k-content');
        //wait 100ms
        // setTimeout(() => {
        //     if (element) element.focus();
        //     console.log('onMount', element);
        // }, 1000);


    };

    const formValidator = (values) => {
        let val = {
            ...(formState?.email?.To?.length < 1 && { "to": `At least one recipient is required in the TO field` }),
            ...(formState?.email?.Subject?.length < 1 && { "subject": `Subject field is required` }),
            ...(!formState?.email?.HTML_Note && { "body": `Email body is required` }),
            ...(fetchTemplateError && { "template": `Error loading template: ${JSON.stringify(fetchTemplateError)}` }),
        }

        //console.log('formValidator', val, formState?.email?.HTML_Note, values);

        if (Object.keys(val).length > 0) return val;
        return null;
    }


    const onIframeInit = (ev: EditorIFrameInitEvent) => {

        //located in public folder
        const stylesheets = [
            './style/emailTemplate.css'
        ];

        stylesheets.forEach(href => {
            if (href && ev.iframe && ev.iframe.contentWindow) {
                const iframeDocument = ev.iframe.contentWindow.document;
                const linkElementWithiFrameStyles = iframeDocument.createElement('link');
                linkElementWithiFrameStyles.rel = 'stylesheet';
                linkElementWithiFrameStyles.type = 'text/css';
                linkElementWithiFrameStyles.href = href;
                iframeDocument.head.appendChild(linkElementWithiFrameStyles);
            }
        });
    }

    //Not for end users
    if (userProfile?.Features.External_Interface) return <></>;

    return (
        <div className='position-relative'>

            {(sendingEmail || isLoading || loadingTemplate) && <PartialSpinny />}
            <Form id={props.formName || 'email-action-form'} onSubmit={handleSend} validator={formValidator} ignoreModified={true}
                render={(formRenderProps) => (
                    <FormElement className='w-100 d-flex flex-column email-action-form p-1 '>
                        {formRenderProps.errors && Object.keys(formRenderProps.errors).length > 0 && (
                            <div className="k-messagebox k-messagebox-error p-2 m-0" role="alert">
                                {Object.keys(formRenderProps.errors).map((key, index) => (
                                    <div key={index}>{formRenderProps.errors[key]}</div>
                                ))}
                            </div>
                        )}
                        {/* From */}
                        <div className='row g-1 align-items-center'>
                            <label className='col-12 col-lg-1'>From:</label>
                            <div className='col-11'>
                                <TextBox disabled size='large' readOnly={true} placeholder='From:' value={formState?.email.From}></TextBox>
                            </div>
                        </div>
                        {/* TO */}
                        <div className='row g-1 align-items-center'>
                            <label className='col-12 col-lg-1'>To:</label>
                            <div className='col-11'>
                                <div className='w-5/6 inline-block' >
                                    <MultiSelect id='To' name='To' size='large' value={formState?.email.To} onChange={handleToChange} tagRender={renderTag} itemRender={renderItem} placeholder='To: Type 3 or more characters to search...' filterable={true} allowCustom={true}
                                        popupSettings={{ className: 'z500', height: 400 }} onFilterChange={onFilter} data={filteredTo} dataItemKey="ID" textField="Full_Name" loading={toLoading} />
                                </div>
                                <ButtonGroup className='w-1/6 inline-block'>
                                    <BadgeContainer >
                                        <Badge>{formState?.email.CC?.length && formState?.email.CC?.length}</Badge>
                                        <Button className='toggle-button' type='button' onClick={() => dispatch(EmailActionSlice.setShowCC(!formState.email.Show_CC))} size='large' fillMode={formState.email.Show_CC ? 'solid' : 'outline'} formNoValidate title='Toggle CC visibility'>CC</Button>
                                    </BadgeContainer>
                                    <BadgeContainer >
                                        <Badge>{formState?.email.BCC && formState?.email.BCC?.length}</Badge>
                                        <Button className='toggle-button' type='button' onClick={() => dispatch(EmailActionSlice.setShowBCC(!formState.email.Show_BCC))} size='large' fillMode={formState.email.Show_BCC ? 'solid' : 'outline'} formNoValidate>BCC</Button>
                                    </BadgeContainer>
                                </ButtonGroup>
                            </div>
                        </div>
                        {/* CC */}
                        {formState?.email.Show_CC &&
                            <div className='row g-1 align-items-center'>
                                <label className='col-12 col-lg-1'>CC:</label>
                                <div className='col-11'>
                                    <MultiSelect size='large' value={formState?.email.CC} onChange={handleCChange} tagRender={renderTag} itemRender={renderItem} placeholder='CC: Type 3 or more characters to search...' filterable={true} allowCustom={true}
                                        popupSettings={{ className: 'z500', height: 400 }} onFilterChange={onFilter} data={filteredCC} dataItemKey="ID" textField="Full_Name" loading={ccLoading} />
                                </div>
                            </div>}
                        {/* BCC */}
                        {formState?.email.Show_BCC &&
                            <div className='row g-1 align-items-center'>
                                <label className='col-12 col-lg-1'>BCC:</label>
                                <div className='col-11'>
                                    <MultiSelect size='large' value={formState?.email.BCC} onChange={handleBCChange} tagRender={renderTag} itemRender={renderItem} placeholder='BCC: Type 3 or more characters to search...' filterable={true} allowCustom={true}
                                        popupSettings={{ className: 'z500', height: 400 }} onFilterChange={onFilter} data={filteredBCC} dataItemKey="ID" textField="Full_Name" loading={bccLoading} />
                                </div>
                            </div>}
                        {/* Subject */}
                        <div className='row g-1 align-items-center'>
                            <label className='col-12 col-lg-1'>Subject:</label>
                            <div className='col-11'>
                                <TextBox value={formState?.email.Subject} size='large' required placeholder='Email Subject:' onChange={(e) => dispatch(EmailActionSlice.setSubject(e.target.value.toString()))}></TextBox>
                            </div>
                        </div>
                        {/* template */}
                        {userProfile?.Features.Email_Templates &&
                            <div className='row g-1 align-items-center'>
                                <label className='col-12 col-lg-1'>Template:</label>
                                <div className='col-11'>
                                    <EmailTemplateList hasDescription={formState?.email.HTML_Note > ''} templateId={formState?.email.Template_Id}
                                        onChange={(e) => dispatch(EmailActionSlice.setTemplateId(e.value?.Id))}
                                    />
                                </div>
                            </div>}
                        <div className='row g-1'>
                            <div className='inline-block col-12 col-lg-1 pl-2'></div>
                            <div className={`inline-block w-full align-top col-12 col-lg-11`}>
                                <Editor
                                    onIFrameInit={onIframeInit}
                                    contentStyle={{ height: '370px' }}
                                    onChange={(e) => dispatch(EmailActionSlice.setNotes(e.html))}
                                    value={formState?.email.HTML_Note || ''}
                                    tools={isMobile ? MobileTools : CommonTools}
                                    onMount={onMount}
                                />
                            </div>

                            {/* <JsonTreeViewer data={formState} title='tree' /> */}
                        </div>
                        {isFileActiveInternal && <div className='row g-1'>
                            <div className='inline-block col-12 col-lg-1 pl-2'></div>
                            <div className={`inline-block w-full align-top col-12 col-lg-11`}>
                                <FileUpload FileType={6} />                            </div>

                        </div>}
                        <div className='row g-1'>
                            <div className='inline-block col-12 col-lg-1 pl-2'></div>
                            {addTimeSettings.canAddTime && <div className='inline-block col pl-2'>
                                <div className='inline-block'>
                                    <Time {...{} as FieldRenderProps} disabled={settings && !settings?.Bill_Time_Enabled} mins={formState?.email.Billable_Time}
                                        onChange={(e) => dispatch(EmailActionSlice.setBillable(e.value))}
                                        label={userProfile?.Features?.Non_Bill_Time ? "Bill (hrs)" : "Time (hrs)"}
                                    ></Time>
                                </div>
                                {userProfile?.Features?.Non_Bill_Time &&
                                    <div className='inline-block  pl-2'>
                                        <Time {...{} as FieldRenderProps} disabled={settings && !settings?.NonBill_Time_Enabled} mins={formState?.email.Non_Billable_Time}
                                            onChange={(e) => dispatch(EmailActionSlice.setNonBillable(e.value))}
                                            label={"Non-Bill (hrs)"}
                                        ></Time>
                                    </div>}
                            </div>}
                        </div>
                    </FormElement >
                )
                }></Form >

        </div >


    );
};

export default EmailForm;